import React, { useState, useCallback } from "react";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Badge from "@mui/material/Badge";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import DownloadIcon from "@mui/icons-material/Download";
import { useLazyQuery } from "@apollo/client";
import {
  MentionInput,
  MentionOption,
} from "../../input/MentionInput/MentionInput";
import { MentionUploadDialog } from "./MentionUploadDialog";
import { MAX_MENTIONS_LIMIT } from "./constants";
import { MentionFilterProps, MentionNamesPayload } from "./types";
import {
  buildMentionsCsv,
  downloadCsv,
  formatMentionCount,
  mergeMentionIds,
} from "./utils";
import { MENTION_ID_QUERY } from "../../../apis/taxonomy/queries";
import { useSnackbar } from "../../Snackbar";
import { TAXONOMY_CLIENT } from "../../../utils/consts";

/**
 * MentionFilter combines the manual mention search with a CSV upload of
 * mention ids and a CSV download of the current selection, the pairing
 * ProductFilter offers for products. Mention names are long, so the icons sit
 * above the search box (as in IntentTopicSelector) and leave it the full width.
 */
export const MentionFilter: React.FC<MentionFilterProps> = ({
  onMentionsSelect,
  selectedMentionIds,
}) => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const { showSuccess, showInfo, showWarning, showError } = useSnackbar();

  const [fetchMentionsForDownload] = useLazyQuery<
    MentionNamesPayload,
    { ids: number[] }
  >(MENTION_ID_QUERY, { context: { clientName: TAXONOMY_CLIENT } });

  const handleMentionsSelect = useCallback(
    (mentions: MentionOption[]) => {
      onMentionsSelect(mentions.map((mention) => Number(mention.value)));
    },
    [onMentionsSelect]
  );

  const handleUploadSuccess = useCallback(
    (uploadedIds: number[]) => {
      const { ids, newCount, addedCount } = mergeMentionIds(
        selectedMentionIds,
        uploadedIds
      );

      if (newCount === 0) {
        showInfo("All uploaded mentions were already selected.");
      } else if (addedCount === 0) {
        showWarning(
          `You can select up to ${formatMentionCount(MAX_MENTIONS_LIMIT)}. Remove some mentions to add more.`
        );
      } else if (addedCount < newCount) {
        onMentionsSelect(ids);
        showWarning(
          `Only ${addedCount.toLocaleString()} of ${formatMentionCount(newCount)} were added. Mentions are limited to ${MAX_MENTIONS_LIMIT.toLocaleString()}.`
        );
      } else {
        onMentionsSelect(ids);
        const alreadySelected = uploadedIds.length - newCount;
        showSuccess(
          `Added ${formatMentionCount(addedCount)} to your selection${alreadySelected > 0 ? ` (${alreadySelected.toLocaleString()} already selected)` : ""}.`
        );
      }
    },
    [onMentionsSelect, selectedMentionIds, showInfo, showSuccess, showWarning]
  );

  const handleDownload = useCallback(async () => {
    if (selectedMentionIds.length === 0) return;

    setDownloadLoading(true);
    try {
      const { data, error } = await fetchMentionsForDownload({
        variables: { ids: selectedMentionIds },
      });
      if (error) throw error;

      const mentions = data?.mention?.data ?? [];
      downloadCsv(
        `selected_mentions_${new Date().toISOString().split("T")[0]}.csv`,
        buildMentionsCsv(mentions)
      );
      showSuccess(`Downloaded ${formatMentionCount(mentions.length)}.`);
    } catch (error) {
      console.error("Mention download failed:", error);
      showError("Download failed. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  }, [selectedMentionIds, fetchMentionsForDownload, showSuccess, showError]);

  return (
    <>
      <Stack spacing={0.5}>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={0.5}
        >
          <Tooltip
            title={`Import mentions from CSV (mention_id column, max ${MAX_MENTIONS_LIMIT.toLocaleString()})`}
            arrow
            placement="top"
          >
            <IconButton
              color="primary"
              onClick={() => setUploadDialogOpen(true)}
              size="small"
              aria-label="Import mentions from CSV"
              data-testid="mentions-upload-btn"
            >
              <FileUploadIcon />
            </IconButton>
          </Tooltip>

          <Tooltip
            title={
              selectedMentionIds.length > 0
                ? `Download ${formatMentionCount(selectedMentionIds.length)} to CSV`
                : "No mentions selected"
            }
            arrow
            placement="top"
          >
            <span>
              <Badge
                data-testid="mentions-selected-count"
                badgeContent={selectedMentionIds.length}
                color="primary"
                max={999}
                sx={{
                  "& .MuiBadge-badge": {
                    fontSize: "0.75rem",
                    minWidth: "16px",
                    height: "16px",
                    padding: "0 4px",
                  },
                }}
              >
                <IconButton
                  color="primary"
                  onClick={handleDownload}
                  disabled={selectedMentionIds.length === 0 || downloadLoading}
                  size="small"
                  aria-label="Download selected mentions to CSV"
                  data-testid="mentions-download-btn"
                >
                  <DownloadIcon />
                </IconButton>
              </Badge>
            </span>
          </Tooltip>
        </Stack>

        {/* width 0 + minWidth 100%: fill the row without letting long chips
            widen the whole Mentions section (and push the icons off-screen). */}
        <Stack sx={{ width: 0, minWidth: "100%" }}>
          <MentionInput
            onMentionsSelect={handleMentionsSelect}
            selectedMentionIds={selectedMentionIds}
          />
        </Stack>
      </Stack>

      <MentionUploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onSuccess={handleUploadSuccess}
      />
    </>
  );
};
